import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import db from "../Firebase";
import { trackEvent } from "../analytics";
import { CardRenderer, CARD_FONTS } from "../CardThemes";
import { useAuth } from "../AuthContext";
import { QRCodeCanvas } from "qrcode.react";
import UpgradeModal from "./UpgradeModal";

const WALLET_BACKEND = "https://myprofilecorner.com/api";


function ProfilePage() {
    const { id } = useParams();
    const [cardData, setCardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [walletUrl, setWalletUrl] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const navigate = useNavigate();
    const { user, logout, isPremium } = useAuth();
    const [cardOwnerId, setCardOwnerId] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const baseUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const cardUrl = `${baseUrl}/${id}`;
    const qrRef = useRef(null);

    useEffect(() => {
        const fetchCard = async () => {
            try {
                let snapshot = await getDocs(
                    query(collection(db, "cards"), where("slug", "==", id))
                );
                if (snapshot.empty) {
                    snapshot = await getDocs(
                        query(collection(db, "cards"), where("phone", "==", id))
                    );
                }
                if (!snapshot.empty) {
                    const doc = snapshot.docs[0].data();
                    setCardData(doc);
                    setCardOwnerId(doc.userId || null);
                    fetchWalletUrl(doc, cardUrl);
                } else {
                    setCardData(null);
                }
            } catch (err) {
                console.error("Error fetching card:", err);
                setCardData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchCard();
    }, [id]);

    const fetchWalletUrl = async (doc, url) => {
        setWalletLoading(true);
        try {
            const res = await fetch(`${WALLET_BACKEND}/generate-pass`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: doc.name,
                    phone: doc.phone,
                    email: doc.email,
                    cardUrl: url,
                    photoUrl: doc.photoUrl || "",
                    designation: doc.designation || "",
                    company: doc.company || "",
                    location: doc.location || "",
                    website: doc.website || "",
                    resumeUrl: doc.resumeUrl || "",
                    socials: doc.socials || {},
                }),
            });
            const data = await res.json();
            setWalletUrl(data.url);
        } catch (err) {
            console.warn("Wallet backend unavailable:", err.message);
        } finally {
            setWalletLoading(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(cardUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        });
    };

    const downloadVCard = () => {
        const vcf = `BEGIN:VCARD\nVERSION:3.0\nFN:${cardData.name}\nTEL:${cardData.phone}\nEMAIL:${cardData.email}\nEND:VCARD`;
        const blob = new Blob([vcf], { type: "text/vcard" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${cardData.name.replace(/\s+/g, "_")}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // const downloadECard = () => {
    //     const qrCanvas = qrRef.current?.querySelector("canvas");
    //     if (!qrCanvas) return;

    //     const W = 300, H = 490;
    //     const scale = 3;
    //     const canvas = document.createElement("canvas");
    //     canvas.width = W * scale;
    //     canvas.height = H * scale;
    //     const ctx = canvas.getContext("2d");
    //     ctx.scale(scale, scale);

    //     const rr = (x, y, w, h, r) => {
    //         ctx.beginPath();
    //         ctx.moveTo(x + r, y);
    //         ctx.lineTo(x + w - r, y);
    //         ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    //         ctx.lineTo(x + w, y + h - r);
    //         ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    //         ctx.lineTo(x + r, y + h);
    //         ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    //         ctx.lineTo(x, y + r);
    //         ctx.quadraticCurveTo(x, y, x + r, y);
    //         ctx.closePath();
    //     };

    //     // Card shadow + background
    //     ctx.shadowColor = "rgba(0,0,0,0.18)";
    //     ctx.shadowBlur = 24;
    //     ctx.fillStyle = "#eef1f6";
    //     rr(0, 0, W, H, 20);
    //     ctx.fill();
    //     ctx.shadowBlur = 0;

    //     // Header (dark gradient, clipped to top rounded)
    //     ctx.save();
    //     rr(0, 0, W, H, 20);
    //     ctx.clip();
    //     const hdrGrad = ctx.createLinearGradient(0, 0, W, 130);
    //     hdrGrad.addColorStop(0, "#13111f");
    //     hdrGrad.addColorStop(1, "#1e1b30");
    //     ctx.fillStyle = hdrGrad;
    //     ctx.fillRect(0, 0, W, 130);
    //     ctx.restore();

    //     // Avatar circle
    //     ctx.fillStyle = "rgba(255,255,255,0.14)";
    //     ctx.beginPath();
    //     ctx.arc(42, 52, 26, 0, Math.PI * 2);
    //     ctx.fill();
    //     ctx.strokeStyle = "rgba(255,255,255,0.25)";
    //     ctx.lineWidth = 1.5;
    //     ctx.stroke();

    //     // Initials in avatar
    //     const initials = (cardData.name || "")
    //         .trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
    //     ctx.fillStyle = "#fff";
    //     ctx.font = "bold 14px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.textBaseline = "middle";
    //     ctx.fillText(initials, 42, 52);

    //     // Name
    //     ctx.fillStyle = "#ffffff";
    //     ctx.font = "bold 14px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillText((cardData.name || "Your Name").toUpperCase().slice(0, 22), 78, 42);

    //     // Designation
    //     ctx.fillStyle = "rgba(255,255,255,0.55)";
    //     ctx.font = "10px Arial, sans-serif";
    //     ctx.fillText((cardData.designation || "").slice(0, 28), 78, 58);

    //     // Company
    //     ctx.fillStyle = "rgba(255,255,255,0.38)";
    //     ctx.font = "10px Arial, sans-serif";
    //     ctx.fillText((cardData.company || "").toUpperCase().slice(0, 26), 78, 72);

    //     // "MyProfileCorner" brand top-right
    //     ctx.fillStyle = "#E8920A";
    //     ctx.font = "bold 8px Arial, sans-serif";
    //     ctx.textAlign = "right";
    //     ctx.fillText("MyProfileCorner", W - 12, 20);

    //     // Google-style colored dots row (decorative)
    //     const dotColors = ["#4285F4", "#EA4335", "#FBBC05", "#34A853"];
    //     dotColors.forEach((c, i) => {
    //         ctx.fillStyle = c;
    //         ctx.beginPath();
    //         ctx.arc(W - 12 - (3 - i) * 11, 32, 4, 0, Math.PI * 2);
    //         ctx.fill();
    //     });

    //     // Divider between header and body
    //     ctx.fillStyle = "#eef1f6";
    //     ctx.fillRect(0, 130, W, 2);

    //     // Body title (name)
    //     ctx.fillStyle = "#1a1a2e";
    //     ctx.font = "bold 17px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillText(cardData.name || "Your Name", W / 2, 162);

    //     // Designation under name (body)
    //     if (cardData.designation) {
    //         ctx.fillStyle = "#6b7280";
    //         ctx.font = "11px Arial, sans-serif";
    //         ctx.fillText(cardData.designation, W / 2, 178);
    //     }

    //     // QR box
    //     const qrSize = 150;
    //     const qrX = (W - qrSize) / 2;
    //     const qrY = 192;
    //     ctx.shadowColor = "rgba(0,0,0,0.10)";
    //     ctx.shadowBlur = 10;
    //     ctx.fillStyle = "#ffffff";
    //     rr(qrX - 12, qrY - 12, qrSize + 24, qrSize + 24, 14);
    //     ctx.fill();
    //     ctx.shadowBlur = 0;
    //     ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    //     // "SCAN TO VIEW PROFILE"
    //     ctx.fillStyle = "#9ca3af";
    //     ctx.font = "bold 8px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("SCAN TO VIEW PROFILE", W / 2, qrY + qrSize + 26);

    //     // Footer line
    //     ctx.strokeStyle = "#e5e7eb";
    //     ctx.lineWidth = 1;
    //     ctx.beginPath();
    //     ctx.moveTo(16, H - 36);
    //     ctx.lineTo(W - 16, H - 36);
    //     ctx.stroke();

    //     // URL bottom-left
    //     ctx.fillStyle = "#9ca3af";
    //     ctx.font = "9px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.fillText(`myprofilecorner.com/${id}`, 16, H - 16);

    //     // Verified badge bottom-right
    //     ctx.fillStyle = "#dcfce7";
    //     rr(W - 82, H - 29, 70, 20, 6);
    //     ctx.fill();
    //     ctx.fillStyle = "#16a34a";
    //     ctx.font = "bold 9px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("✓ VERIFIED", W - 47, H - 15);

    //     canvas.toBlob((blob) => {
    //         const url = URL.createObjectURL(blob);
    //         const a = document.createElement("a");
    //         a.href = url;
    //         a.download = `${(cardData.name || "ecard").replace(/\s+/g, "_")}-ecard.png`;
    //         document.body.appendChild(a);
    //         a.click();
    //         document.body.removeChild(a);
    //         URL.revokeObjectURL(url);
    //     }, "image/png");
    // };


    // const downloadECard = () => {
    //     const qrCanvas = qrRef.current?.querySelector("canvas");
    //     if (!qrCanvas) return;

    //     const W = 360, H = 600;
    //     const scale = 3;
    //     const canvas = document.createElement("canvas");
    //     canvas.width = W * scale;
    //     canvas.height = H * scale;
    //     const ctx = canvas.getContext("2d");
    //     ctx.scale(scale, scale);

    //     const rr = (x, y, w, h, r) => {
    //         ctx.beginPath();
    //         ctx.moveTo(x + r, y);
    //         ctx.lineTo(x + w - r, y);
    //         ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    //         ctx.lineTo(x + w, y + h - r);
    //         ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    //         ctx.lineTo(x + r, y + h);
    //         ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    //         ctx.lineTo(x, y + r);
    //         ctx.quadraticCurveTo(x, y, x + r, y);
    //         ctx.closePath();
    //     };

    //     // ── Card base ─────────────────────────────────────
    //     ctx.fillStyle = "#F7F6F2";
    //     rr(0, 0, W, H, 20);
    //     ctx.fill();

    //     // ── Header: navy → indigo gradient ────────────────
    //     ctx.save();
    //     rr(0, 0, W, H, 20);
    //     ctx.clip();
    //     const hdrGrad = ctx.createLinearGradient(0, 0, W, 170);
    //     hdrGrad.addColorStop(0, "#0D0B1A");
    //     hdrGrad.addColorStop(0.5, "#1A1535");
    //     hdrGrad.addColorStop(1, "#221D42");
    //     ctx.fillStyle = hdrGrad;
    //     ctx.fillRect(0, 0, W, 170);

    //     // Radial purple glow top-right
    //     const glow = ctx.createRadialGradient(W, 0, 0, W, 0, 220);
    //     glow.addColorStop(0, "rgba(130,100,255,0.18)");
    //     glow.addColorStop(1, "rgba(130,100,255,0)");
    //     ctx.fillStyle = glow;
    //     ctx.fillRect(0, 0, W, 170);

    //     // Dot-grid texture overlay
    //     ctx.fillStyle = "rgba(255,255,255,0.035)";
    //     for (let dx = 14; dx < W; dx += 18) {
    //         for (let dy = 10; dy < 170; dy += 18) {
    //             ctx.beginPath();
    //             ctx.arc(dx, dy, 1.2, 0, Math.PI * 2);
    //             ctx.fill();
    //         }
    //     }
    //     ctx.restore();

    //     // Gold accent divider line
    //     const goldGrad = ctx.createLinearGradient(0, 0, W, 0);
    //     goldGrad.addColorStop(0, "rgba(255,190,60,0)");
    //     goldGrad.addColorStop(0.3, "rgba(255,190,60,0.9)");
    //     goldGrad.addColorStop(0.7, "rgba(255,190,60,0.9)");
    //     goldGrad.addColorStop(1, "rgba(255,190,60,0)");
    //     ctx.fillStyle = goldGrad;
    //     ctx.fillRect(0, 169, W, 2);

    //     // ── Avatar with gold ring ──────────────────────────
    //     const cx = 54, cy = 68, ar = 34;
    //     const ringGrad = ctx.createLinearGradient(cx - ar, cy - ar, cx + ar, cy + ar);
    //     ringGrad.addColorStop(0, "#FFD060");
    //     ringGrad.addColorStop(1, "#E8920A");
    //     ctx.strokeStyle = ringGrad;
    //     ctx.lineWidth = 2;
    //     ctx.beginPath();
    //     ctx.arc(cx, cy, ar + 4, 0, Math.PI * 2);
    //     ctx.stroke();

    //     const avGrad = ctx.createLinearGradient(cx - ar, cy - ar, cx + ar, cy + ar);
    //     avGrad.addColorStop(0, "#2E2660");
    //     avGrad.addColorStop(1, "#4A3FA0");
    //     ctx.fillStyle = avGrad;
    //     ctx.beginPath();
    //     ctx.arc(cx, cy, ar, 0, Math.PI * 2);
    //     ctx.fill();

    //     const initials = (cardData.name || "")
    //         .trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
    //     ctx.fillStyle = "#fff";
    //     ctx.font = "bold 16px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.textBaseline = "middle";
    //     ctx.fillText(initials, cx, cy);

    //     // ── Header text ───────────────────────────────────
    //     ctx.textAlign = "left";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillStyle = "#FFFFFF";
    //     ctx.font = "bold 15px Arial, sans-serif";
    //     ctx.fillText((cardData.name || "Your Name").toUpperCase().slice(0, 22), 100, 52);

    //     ctx.fillStyle = "rgba(255,255,255,0.55)";
    //     ctx.font = "11px Arial, sans-serif";
    //     ctx.fillText((cardData.designation || "").slice(0, 30), 100, 70);

    //     ctx.fillStyle = "rgba(255,190,60,0.8)";
    //     ctx.font = "10px Arial, sans-serif";
    //     ctx.fillText((cardData.company || "").toUpperCase().slice(0, 24), 100, 88);

    //     // Active status badge
    //     ctx.fillStyle = "rgba(52,211,153,0.18)";
    //     rr(100, 100, 80, 20, 10);
    //     ctx.fill();
    //     ctx.strokeStyle = "rgba(52,211,153,0.4)";
    //     ctx.lineWidth = 0.8;
    //     rr(100, 100, 80, 20, 10);
    //     ctx.stroke();
    //     ctx.fillStyle = "#34D399";
    //     ctx.font = "bold 9px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.fillText("● ACTIVE", 110, 113);

    //     // Brand + dots top-right
    //     ctx.fillStyle = "#FFB830";
    //     ctx.font = "bold 9px Arial, sans-serif";
    //     ctx.textAlign = "right";
    //     ctx.fillText("MyProfileCorner", W - 16, 22);
    //     const dotColors = ["#4285F4", "#EA4335", "#FBBC05", "#34A853"];
    //     dotColors.forEach((c, i) => {
    //         ctx.fillStyle = c;
    //         ctx.beginPath();
    //         ctx.arc(W - 16 - (3 - i) * 13, 36, 4.5, 0, Math.PI * 2);
    //         ctx.fill();
    //     });

    //     // ── Body: name + designation ──────────────────────
    //     ctx.fillStyle = "#111827";
    //     ctx.font = "bold 20px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillText(cardData.name || "Your Name", W / 2, 210);

    //     ctx.fillStyle = "#6B7280";
    //     ctx.font = "12px Arial, sans-serif";
    //     const subline = [cardData.designation, cardData.company].filter(Boolean).join(" · ");
    //     ctx.fillText(subline.slice(0, 40), W / 2, 230);

    //     // Dashed separator
    //     ctx.strokeStyle = "#E5E7EB";
    //     ctx.lineWidth = 0.8;
    //     ctx.setLineDash([2, 6]);
    //     ctx.beginPath();
    //     ctx.moveTo(40, 246);
    //     ctx.lineTo(W - 40, 246);
    //     ctx.stroke();
    //     ctx.setLineDash([]);

    //     // ── QR card ──────────────────────────────────────
    //     const qrSize = 160;
    //     const qrX = (W - qrSize) / 2;
    //     const qrY = 262;

    //     // Shadow
    //     ctx.fillStyle = "rgba(0,0,0,0.06)";
    //     rr(qrX - 14 + 3, qrY - 14 + 3, qrSize + 28, qrSize + 28, 16);
    //     ctx.fill();
    //     // White card
    //     ctx.fillStyle = "#FFFFFF";
    //     rr(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28, 16);
    //     ctx.fill();
    //     ctx.strokeStyle = "#E5E7EB";
    //     ctx.lineWidth = 0.8;
    //     rr(qrX - 14, qrY - 14, qrSize + 28, qrSize + 28, 16);
    //     ctx.stroke();
    //     ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    //     ctx.fillStyle = "#9CA3AF";
    //     ctx.font = "bold 9px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("SCAN TO VIEW PROFILE", W / 2, qrY + qrSize + 30);

    //     // ── Contact icons row ─────────────────────────────
    //     const iconY = qrY + qrSize + 50;
    //     const icons = [
    //         { label: "Email", color: "#3B82F6" },
    //         { label: "Phone", color: "#10B981" },
    //         { label: "Website", color: "#8B5CF6" },
    //         { label: "Share", color: "#F59E0B" },
    //     ];
    //     const iW = 56;
    //     const totalW = icons.length * iW + (icons.length - 1) * 12;
    //     const startX = (W - totalW) / 2;
    //     icons.forEach((icon, i) => {
    //         const ix = startX + i * (iW + 12);
    //         ctx.fillStyle = icon.color + "18";
    //         rr(ix, iconY, iW, 44, 10);
    //         ctx.fill();
    //         ctx.strokeStyle = icon.color + "40";
    //         ctx.lineWidth = 0.8;
    //         rr(ix, iconY, iW, 44, 10);
    //         ctx.stroke();
    //         ctx.fillStyle = icon.color;
    //         ctx.font = "bold 9px Arial, sans-serif";
    //         ctx.textAlign = "center";
    //         ctx.fillText(icon.label, ix + iW / 2, iconY + 28);
    //     });

    //     // ── Footer ────────────────────────────────────────
    //     const footY = H - 50;
    //     const footGrad = ctx.createLinearGradient(0, 0, W, 0);
    //     footGrad.addColorStop(0, "rgba(229,231,235,0)");
    //     footGrad.addColorStop(0.5, "rgba(229,231,235,1)");
    //     footGrad.addColorStop(1, "rgba(229,231,235,0)");
    //     ctx.strokeStyle = footGrad;
    //     ctx.lineWidth = 0.8;
    //     ctx.beginPath();
    //     ctx.moveTo(16, footY);
    //     ctx.lineTo(W - 16, footY);
    //     ctx.stroke();

    //     ctx.fillStyle = "#9CA3AF";
    //     ctx.font = "9px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillText(`myprofilecorner.com/${id}`, 20, footY + 24);

    //     // Gradient verified badge
    //     const vBadgeX = W - 90;
    //     const vGrad = ctx.createLinearGradient(vBadgeX, 0, W - 16, 0);
    //     vGrad.addColorStop(0, "#D1FAE5");
    //     vGrad.addColorStop(1, "#A7F3D0");
    //     ctx.fillStyle = vGrad;
    //     rr(vBadgeX, footY + 9, 74, 22, 8);
    //     ctx.fill();
    //     ctx.fillStyle = "#065F46";
    //     ctx.font = "bold 9px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("✓ VERIFIED", vBadgeX + 37, footY + 23);

    //     // ── Export ────────────────────────────────────────
    //     canvas.toBlob((blob) => {
    //         const url = URL.createObjectURL(blob);
    //         const a = document.createElement("a");
    //         a.href = url;
    //         a.download = `${(cardData.name || "ecard").replace(/\s+/g, "_")}-ecard.png`;
    //         document.body.appendChild(a);
    //         a.click();
    //         document.body.removeChild(a);
    //         URL.revokeObjectURL(url);
    //     }, "image/png");
    // };


    // ─────────────────────────────────────────────────────────────
    //  Premium E-Card Download  —  MyProfileCorner
    //  Card size: 360 × 560 px  (exported at 3× = 1080 × 1680)
    //
    //  Props used from outer scope:
    //    qrRef      — ref to the container holding the QR <canvas>
    //    cardData   — { name, designation, company }
    //    id         — profile slug, e.g. "alex-chen"
    // ─────────────────────────────────────────────────────────────

    // const downloadECard = async () => {
    //     const qrCanvas = qrRef.current?.querySelector("canvas");
    //     if (!qrCanvas) return;

    //     // Load profile photo — served from our own domain via /media/ proxy (no CORS issue)
    //     let profileImg = null;
    //     if (cardData.photoUrl) {
    //         const { toProxyUrl } = await import("../proxyUrl");
    //         profileImg = await new Promise((resolve) => {
    //             const img = new Image();
    //             img.onload = () => resolve(img);
    //             img.onerror = () => resolve(null);
    //             img.src = toProxyUrl(cardData.photoUrl);
    //         });
    //     }

    //     const W = 360, H = 600;
    //     const scale = 3;
    //     const canvas = document.createElement("canvas");
    //     canvas.width = W * scale;
    //     canvas.height = H * scale;
    //     const ctx = canvas.getContext("2d");
    //     ctx.scale(scale, scale);

    //     // ── Utility: rounded rect path ────────────────────────────
    //     const rr = (x, y, w, h, r) => {
    //         ctx.beginPath();
    //         ctx.moveTo(x + r, y);
    //         ctx.lineTo(x + w - r, y);
    //         ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    //         ctx.lineTo(x + w, y + h - r);
    //         ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    //         ctx.lineTo(x + r, y + h);
    //         ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    //         ctx.lineTo(x, y + r);
    //         ctx.quadraticCurveTo(x, y, x + r, y);
    //         ctx.closePath();
    //     };

    //     // ── 1. Card base — pure white ─────────────────────────────
    //     ctx.fillStyle = "#FFFFFF";
    //     rr(0, 0, W, H, 20);
    //     ctx.fill();

    //     // ── 2. Header: dark navy → indigo gradient ────────────────
    //     ctx.save();
    //     rr(0, 0, W, H, 20);
    //     ctx.clip();

    //     const hdrGrad = ctx.createLinearGradient(0, 0, W, 180);
    //     hdrGrad.addColorStop(0, "#0D0B1A");
    //     hdrGrad.addColorStop(0.55, "#1A1535");
    //     hdrGrad.addColorStop(1, "#221D42");
    //     ctx.fillStyle = hdrGrad;
    //     ctx.fillRect(0, 0, W, 180);

    //     // Radial purple glow top-right corner
    //     const glow = ctx.createRadialGradient(W, 0, 0, W, 0, 230);
    //     glow.addColorStop(0, "rgba(120,90,255,0.20)");
    //     glow.addColorStop(1, "rgba(120,90,255,0)");
    //     ctx.fillStyle = glow;
    //     ctx.fillRect(0, 0, W, 180);

    //     // Dot-grid texture overlay
    //     ctx.fillStyle = "rgba(255,255,255,0.032)";
    //     for (let dx = 14; dx < W; dx += 18) {
    //         for (let dy = 10; dy < 180; dy += 18) {
    //             ctx.beginPath();
    //             ctx.arc(dx, dy, 1.2, 0, Math.PI * 2);
    //             ctx.fill();
    //         }
    //     }
    //     ctx.restore();

    //     // Gold accent divider line (fades at both ends)
    //     const goldGrad = ctx.createLinearGradient(0, 0, W, 0);
    //     goldGrad.addColorStop(0, "rgba(255,190,60,0)");
    //     goldGrad.addColorStop(0.25, "rgba(255,190,60,0.85)");
    //     goldGrad.addColorStop(0.75, "rgba(255,190,60,0.85)");
    //     goldGrad.addColorStop(1, "rgba(255,190,60,0)");
    //     ctx.fillStyle = goldGrad;
    //     ctx.fillRect(0, 179, W, 1.5);

    //     // ── 3. Avatar with gold ring ──────────────────────────────
    //     const cx = 56, cy = 78, ar = 36;

    //     // Gold gradient ring
    //     const ringGrad = ctx.createLinearGradient(cx - ar, cy - ar, cx + ar, cy + ar);
    //     ringGrad.addColorStop(0, "#FFD060");
    //     ringGrad.addColorStop(1, "#E8920A");
    //     ctx.strokeStyle = ringGrad;
    //     ctx.lineWidth = 2.2;
    //     ctx.beginPath();
    //     ctx.arc(cx, cy, ar + 5, 0, Math.PI * 2);
    //     ctx.stroke();

    //     // Avatar — photo if available, else initials
    //     ctx.save();
    //     ctx.beginPath();
    //     ctx.arc(cx, cy, ar, 0, Math.PI * 2);
    //     ctx.clip();
    //     if (profileImg) {
    //         // object-fit: cover — scale to fill circle, center-crop the overflow
    //         const diameter = ar * 2;
    //         const scale = Math.max(diameter / profileImg.naturalWidth, diameter / profileImg.naturalHeight);
    //         const drawW = profileImg.naturalWidth * scale;
    //         const drawH = profileImg.naturalHeight * scale;
    //         const drawX = cx - ar - (drawW - diameter) / 2;
    //         const drawY = cy - ar - (drawH - diameter) / 2;
    //         ctx.drawImage(profileImg, drawX, drawY, drawW, drawH);
    //     } else {
    //         const avGrad = ctx.createLinearGradient(cx - ar, cy - ar, cx + ar, cy + ar);
    //         avGrad.addColorStop(0, "#2E2660");
    //         avGrad.addColorStop(1, "#4A3FA0");
    //         ctx.fillStyle = avGrad;
    //         ctx.fillRect(cx - ar, cy - ar, ar * 2, ar * 2);
    //         const initials = (cardData.name || "")
    //             .trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
    //         ctx.fillStyle = "#fff";
    //         ctx.font = "bold 17px Arial, sans-serif";
    //         ctx.textAlign = "center";
    //         ctx.textBaseline = "middle";
    //         ctx.fillText(initials, cx, cy);
    //     }
    //     ctx.restore();

    //     // ── 4. Header text ────────────────────────────────────────
    //     ctx.textAlign = "left";
    //     ctx.textBaseline = "alphabetic";

    //     // Name
    //     ctx.fillStyle = "#FFFFFF";
    //     ctx.font = "bold 15px Arial, sans-serif";
    //     ctx.fillText((cardData.name || "Your Name").toUpperCase().slice(0, 22), 106, 58);

    //     // Designation
    //     ctx.fillStyle = "rgba(255,255,255,0.52)";
    //     ctx.font = "11px Arial, sans-serif";
    //     ctx.fillText((cardData.designation || "").slice(0, 32), 106, 76);

    //     // Company — gold tint
    //     ctx.fillStyle = "rgba(255,185,55,0.85)";
    //     ctx.font = "10px Arial, sans-serif";
    //     ctx.fillText((cardData.company || "").toUpperCase().slice(0, 26), 106, 93);

    //     // Active status badge
    //     ctx.fillStyle = "rgba(52,211,153,0.15)";
    //     rr(106, 102, 76, 19, 9);
    //     ctx.fill();
    //     ctx.strokeStyle = "rgba(52,211,153,0.38)";
    //     ctx.lineWidth = 0.8;
    //     rr(106, 102, 76, 19, 9);
    //     ctx.stroke();
    //     ctx.fillStyle = "#34D399";
    //     ctx.font = "bold 8.5px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.fillText("● ACTIVE", 117, 115);

    //     // Brand label + coloured dots — top right
    //     ctx.fillStyle = "#FFB830";
    //     ctx.font = "bold 8.5px Arial, sans-serif";
    //     ctx.textAlign = "right";
    //     ctx.fillText("MyProfileCorner", W - 16, 22);

    //     const dotColors = ["#4285F4", "#EA4335", "#FBBC05", "#34A853"];
    //     dotColors.forEach((c, i) => {
    //         ctx.fillStyle = c;
    //         ctx.beginPath();
    //         ctx.arc(W - 16 - (3 - i) * 13, 36, 4.5, 0, Math.PI * 2);
    //         ctx.fill();
    //     });

    //     // ── 5. Body: subtle top shadow where header meets white ───
    //     const bodyShadow = ctx.createLinearGradient(0, 180, 0, 236);
    //     bodyShadow.addColorStop(0, "rgba(0,0,0,0.04)");
    //     bodyShadow.addColorStop(1, "rgba(0,0,0,0)");
    //     ctx.fillStyle = bodyShadow;
    //     ctx.fillRect(0, 180, W, 56);

    //     // ── 6. QR card ────────────────────────────────────────────
    //     // Layout: 374px content area (180→554), evenly divided
    //     //   42px top margin → QR card (188px) → 24px → label → 24px → icons → 42px bottom
    //     const qrSize = 162;
    //     const qrX = (W - qrSize) / 2;
    //     const qrY = 235;

    //     // Drop shadow (offset fill trick)
    //     ctx.fillStyle = "rgba(0,0,0,0.055)";
    //     rr(qrX - 13 + 3, qrY - 13 + 3, qrSize + 26, qrSize + 26, 16);
    //     ctx.fill();

    //     // White card
    //     ctx.fillStyle = "#FFFFFF";
    //     rr(qrX - 13, qrY - 13, qrSize + 26, qrSize + 26, 16);
    //     ctx.fill();
    //     ctx.strokeStyle = "#EBEBEB";
    //     ctx.lineWidth = 1;
    //     rr(qrX - 13, qrY - 13, qrSize + 26, qrSize + 26, 16);
    //     ctx.stroke();

    //     // QR code image
    //     ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    //     // "Scan" label
    //     ctx.fillStyle = "#B0B7C3";
    //     ctx.font = "bold 8.5px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("SCAN TO VIEW PROFILE", W / 2, qrY + qrSize + 37);

    //     // ── 7. Icon row — dynamic, label-free, dual-tone ─────────────
    //     const iconList = [];
    //     if (cardData.email)              iconList.push({ type: "email",     color: "#3B82F6", light: "#93C5FD" });
    //     if (cardData.phone)              iconList.push({ type: "phone",     color: "#10B981", light: "#6EE7B7" });
    //     if (cardData.website)            iconList.push({ type: "globe",     color: "#8B5CF6", light: "#C4B5FD" });
    //     if (cardData.socials?.whatsapp)  iconList.push({ type: "whatsapp",  color: "#25D366", light: "#86EFAC" });
    //     if (cardData.socials?.linkedin)  iconList.push({ type: "linkedin",  color: "#0A66C2", light: "#60A5FA" });
    //     if (cardData.socials?.twitter)   iconList.push({ type: "twitter",   color: "#1D9BF0", light: "#7DD3FC" });
    //     if (cardData.socials?.instagram) iconList.push({ type: "instagram", color: "#E1306C", light: "#F9A8D4" });
    //     if (cardData.socials?.github)    iconList.push({ type: "github",    color: "#24292E", light: "#9CA3AF" });
    //     if (cardData.socials?.facebook)  iconList.push({ type: "facebook",  color: "#1877F2", light: "#93C5FD" });
    //     if (cardData.socials?.youtube)   iconList.push({ type: "youtube",   color: "#FF0000", light: "#FCA5A5" });

    //     const displayIcons = iconList.slice(0, 7);

    //     if (displayIcons.length > 0) {
    //         const ir = displayIcons.length <= 4 ? 22 : displayIcons.length <= 6 ? 19 : 16;
    //         const iconGap = displayIcons.length <= 4 ? 16 : 11;
    //         const iconTotalW = displayIcons.length * ir * 2 + (displayIcons.length - 1) * iconGap;
    //         const iconStartX = (W - iconTotalW) / 2;
    //         const iconRowY = qrY + qrSize + 71;

    //         displayIcons.forEach((icon, idx) => {
    //             const icx = iconStartX + idx * (ir * 2 + iconGap) + ir;
    //             const icy = iconRowY + ir;

    //             // Dual-tone circle with glow
    //             const cg = ctx.createLinearGradient(icx - ir, icy - ir, icx + ir, icy + ir);
    //             cg.addColorStop(0, icon.light);
    //             cg.addColorStop(1, icon.color);
    //             ctx.shadowColor = icon.color + "55";
    //             ctx.shadowBlur = 8;
    //             ctx.fillStyle = cg;
    //             ctx.beginPath();
    //             ctx.arc(icx, icy, ir, 0, Math.PI * 2);
    //             ctx.fill();
    //             ctx.shadowBlur = 0;

    //             // White icon inside circle
    //             ctx.save();
    //             ctx.strokeStyle = "#fff";
    //             ctx.fillStyle = "#fff";
    //             const lw = ir >= 20 ? 1.4 : 1.2;
    //             ctx.lineWidth = lw;
    //             ctx.lineCap = "round";
    //             ctx.lineJoin = "round";
    //             const s = ir * 0.44;

    //             if (icon.type === "email") {
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - s, icy - s * 0.7, s * 2, s * 1.4, 2);
    //                 ctx.fill();
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.stroke();
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s, icy - s * 0.7);
    //                 ctx.lineTo(icx, icy + s * 0.1);
    //                 ctx.lineTo(icx + s, icy - s * 0.7);
    //                 ctx.stroke();

    //             } else if (icon.type === "phone") {
    //                 const pw = s * 0.96, ph = s * 1.6;
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - pw / 2, icy - ph / 2, pw, ph, pw * 0.3);
    //                 ctx.fill();
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.stroke();
    //                 ctx.fillStyle = "rgba(255,255,255,0.38)";
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - pw / 2 + 1.8, icy - ph / 2 + 5, pw - 3.6, ph - 11, 1.2);
    //                 ctx.fill();
    //                 ctx.fillStyle = "#fff";
    //                 ctx.beginPath();
    //                 ctx.arc(icx, icy - ph / 2 + 2.5, 1.1, 0, Math.PI * 2);
    //                 ctx.fill();
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - 2.8, icy + ph / 2 - 2.5);
    //                 ctx.lineTo(icx + 2.8, icy + ph / 2 - 2.5);
    //                 ctx.lineWidth = lw + 0.3;
    //                 ctx.stroke();

    //             } else if (icon.type === "globe") {
    //                 ctx.fillStyle = "rgba(255,255,255,0.18)";
    //                 ctx.beginPath();
    //                 ctx.arc(icx, icy, s, 0, Math.PI * 2);
    //                 ctx.fill();
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.stroke();
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s, icy);
    //                 ctx.lineTo(icx + s, icy);
    //                 ctx.stroke();
    //                 ctx.beginPath();
    //                 ctx.ellipse(icx, icy, s * 0.42, s, 0, 0, Math.PI * 2);
    //                 ctx.stroke();
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s * 0.7, icy - s * 0.5);
    //                 ctx.quadraticCurveTo(icx, icy - s * 0.28, icx + s * 0.7, icy - s * 0.5);
    //                 ctx.stroke();

    //             } else if (icon.type === "whatsapp") {
    //                 // Speech bubble + phone
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.beginPath();
    //                 ctx.arc(icx, icy - s * 0.1, s, 0, Math.PI * 2);
    //                 ctx.fill();
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.stroke();
    //                 // Tail
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s * 0.3, icy + s * 0.65);
    //                 ctx.lineTo(icx - s * 0.7, icy + s * 1.0);
    //                 ctx.lineTo(icx + s * 0.1, icy + s * 0.72);
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.fill();
    //                 ctx.stroke();
    //                 // Phone handset inside bubble
    //                 ctx.fillStyle = "#fff";
    //                 ctx.font = `bold ${s * 0.9}px Arial`;
    //                 ctx.textAlign = "center";
    //                 ctx.textBaseline = "middle";
    //                 ctx.fillText("✆", icx, icy - s * 0.12);

    //             } else if (icon.type === "linkedin") {
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - s, icy - s, s * 2, s * 2, s * 0.28);
    //                 ctx.fill();
    //                 ctx.fillStyle = "#fff";
    //                 ctx.font = `bold ${s * 1.3}px Arial`;
    //                 ctx.textAlign = "center";
    //                 ctx.textBaseline = "middle";
    //                 ctx.fillText("in", icx, icy);

    //             } else if (icon.type === "twitter") {
    //                 // X shape
    //                 ctx.lineWidth = lw + 0.4;
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s * 0.72, icy - s * 0.72);
    //                 ctx.lineTo(icx + s * 0.72, icy + s * 0.72);
    //                 ctx.stroke();
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx + s * 0.72, icy - s * 0.72);
    //                 ctx.lineTo(icx - s * 0.72, icy + s * 0.72);
    //                 ctx.stroke();

    //             } else if (icon.type === "instagram") {
    //                 // Rounded square
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.lineWidth = lw;
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - s, icy - s, s * 2, s * 2, s * 0.42);
    //                 ctx.stroke();
    //                 // Lens circle
    //                 ctx.beginPath();
    //                 ctx.arc(icx, icy, s * 0.48, 0, Math.PI * 2);
    //                 ctx.stroke();
    //                 // Top-right dot
    //                 ctx.fillStyle = "#fff";
    //                 ctx.beginPath();
    //                 ctx.arc(icx + s * 0.62, icy - s * 0.62, lw * 0.8, 0, Math.PI * 2);
    //                 ctx.fill();

    //             } else if (icon.type === "github") {
    //                 ctx.fillStyle = "#fff";
    //                 ctx.font = `bold ${s * 1.1}px Arial`;
    //                 ctx.textAlign = "center";
    //                 ctx.textBaseline = "middle";
    //                 ctx.fillText("GH", icx, icy);

    //             } else if (icon.type === "facebook") {
    //                 ctx.fillStyle = "#fff";
    //                 ctx.font = `bold ${s * 1.5}px Arial`;
    //                 ctx.textAlign = "center";
    //                 ctx.textBaseline = "middle";
    //                 ctx.fillText("f", icx + s * 0.1, icy);

    //             } else if (icon.type === "youtube") {
    //                 // Play button in rounded rect
    //                 ctx.fillStyle = "rgba(255,255,255,0.22)";
    //                 ctx.beginPath();
    //                 ctx.roundRect(icx - s, icy - s * 0.68, s * 2, s * 1.36, s * 0.3);
    //                 ctx.fill();
    //                 ctx.strokeStyle = "#fff";
    //                 ctx.stroke();
    //                 ctx.fillStyle = "#fff";
    //                 ctx.beginPath();
    //                 ctx.moveTo(icx - s * 0.3, icy - s * 0.38);
    //                 ctx.lineTo(icx + s * 0.5, icy);
    //                 ctx.lineTo(icx - s * 0.3, icy + s * 0.38);
    //                 ctx.closePath();
    //                 ctx.fill();
    //             }

    //             ctx.restore();
    //         });
    //     }

    //     // ── 8. Footer ─────────────────────────────────────────────
    //     const footY = H - 46;

    //     // Fading separator line
    //     const fGrad = ctx.createLinearGradient(0, 0, W, 0);
    //     fGrad.addColorStop(0, "rgba(210,210,210,0)");
    //     fGrad.addColorStop(0.5, "rgba(210,210,210,1)");
    //     fGrad.addColorStop(1, "rgba(210,210,210,0)");
    //     ctx.strokeStyle = fGrad;
    //     ctx.lineWidth = 0.8;
    //     ctx.beginPath();
    //     ctx.moveTo(16, footY);
    //     ctx.lineTo(W - 16, footY);
    //     ctx.stroke();

    //     // Profile URL — bottom left
    //     ctx.fillStyle = "#B0B7C3";
    //     ctx.font = "9px Arial, sans-serif";
    //     ctx.textAlign = "left";
    //     ctx.textBaseline = "alphabetic";
    //     ctx.fillText(`myprofilecorner.com/${id}`, 24, footY + 22);

    //     // Verified badge — bottom right (gradient green pill)
    //     const vx = W - 92;
    //     const vGrad = ctx.createLinearGradient(vx, 0, W - 16, 0);
    //     vGrad.addColorStop(0, "#D1FAE5");
    //     vGrad.addColorStop(1, "#A7F3D0");
    //     ctx.fillStyle = vGrad;
    //     rr(vx, footY + 8, 70, 20, 8);
    //     ctx.fill();
    //     ctx.fillStyle = "#065F46";
    //     ctx.font = "bold 8.5px Arial, sans-serif";
    //     ctx.textAlign = "center";
    //     ctx.fillText("✓ VERIFIED", vx + 35, footY + 21);

    //     // ── 9. Export as PNG ──────────────────────────────────────
    //     canvas.toBlob((blob) => {
    //         const url = URL.createObjectURL(blob);
    //         const a = document.createElement("a");
    //         a.href = url;
    //         a.download = `${(cardData.name || "ecard").replace(/\s+/g, "_")}-ecard.png`;
    //         document.body.appendChild(a);
    //         a.click();
    //         document.body.removeChild(a);
    //         URL.revokeObjectURL(url);
    //     }, "image/png");
    // };

    const downloadECard = async () => {
        const qrCanvas = qrRef.current?.querySelector("canvas");
        if (!qrCanvas) return;

        let profileImg = null;
        if (cardData.photoUrl) {
            const { toProxyUrl } = await import("../proxyUrl");
            profileImg = await new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = () => resolve(null);
                img.src = toProxyUrl(cardData.photoUrl);
            });
        }

        const W = 360, H = 620;
        const scale = 3;
        const canvas = document.createElement("canvas");
        canvas.width = W * scale;
        canvas.height = H * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);

        // ── Utility: rounded rect path ────────────────────────────────
        const rr = (x, y, w, h, r) => {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
        };

        // ── 1. Card base ──────────────────────────────────────────────
        // rr(0, 0, W, H, 36);
        // ctx.fillStyle = "#080810";
        // ctx.fill();

        // ctx.save();
        // rr(0, 0, W, H, 36);
        // ctx.clip();

        ctx.fillStyle = "#080810";
        ctx.fillRect(0, 0, W, H);

        ctx.save();

        // ── 2. Hero background ────────────────────────────────────────
        const heroBg = ctx.createLinearGradient(0, 0, W, 128);
        heroBg.addColorStop(0, "#0c0c18");
        heroBg.addColorStop(1, "#10102a");
        ctx.fillStyle = heroBg;
        ctx.fillRect(0, 0, W, 128);

        // Orb glow top-right
        const orb1 = ctx.createRadialGradient(W + 40, -30, 0, W + 40, -30, 220);
        orb1.addColorStop(0, "rgba(196,140,80,0.18)");
        orb1.addColorStop(1, "rgba(196,140,80,0)");
        ctx.fillStyle = orb1;
        ctx.fillRect(0, 0, W, 128);

        // Orb glow bottom-left
        const orb2 = ctx.createRadialGradient(-30, 140, 0, -30, 140, 180);
        orb2.addColorStop(0, "rgba(100,80,220,0.14)");
        orb2.addColorStop(1, "rgba(100,80,220,0)");
        ctx.fillStyle = orb2;
        ctx.fillRect(0, 0, W, 128);

        // Stars
        const starData = Array.from({ length: 48 }, () => ({
            x: Math.random() * W,
            y: Math.random() * 128,
            r: Math.random() * 0.9 + 0.3,
            a: Math.random() * 0.7 + 0.2,
        }));
        starData.forEach(({ x, y, r, a }) => {
            ctx.fillStyle = `rgba(255,255,255,${a})`;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        });

        ctx.restore();

        // ── 3. Avatar: gold conic ring + photo/initials ───────────────
        const cx = 52, cy = 64, ar = 36;

        const ringColors = ["#b8922a", "#f0c96e", "#c8a03a", "#e8ba55", "#9a7820", "#f0c96e", "#b8922a"];
        for (let i = 0; i < ringColors.length; i++) {
            const startA = (i / ringColors.length) * Math.PI * 2 - Math.PI / 2;
            const endA = ((i + 1) / ringColors.length) * Math.PI * 2 - Math.PI / 2;
            ctx.beginPath();
            ctx.arc(cx, cy, ar + 4, startA, endA);
            ctx.strokeStyle = ringColors[i];
            ctx.lineWidth = 3;
            ctx.stroke();
        }

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, ar, 0, Math.PI * 2);
        ctx.clip();

        if (profileImg) {
            const diameter = ar * 2;
            const imgScale = Math.max(diameter / profileImg.naturalWidth, diameter / profileImg.naturalHeight);
            const drawW = profileImg.naturalWidth * imgScale;
            const drawH = profileImg.naturalHeight * imgScale;
            const drawX = cx - ar - (drawW - diameter) / 2;
            const drawY = cy - ar - (drawH - diameter) / 2;
            ctx.drawImage(profileImg, drawX, drawY, drawW, drawH);
        } else {
            const avGrad = ctx.createLinearGradient(cx - ar, cy - ar, cx + ar, cy + ar);
            avGrad.addColorStop(0, "#1a1025");
            avGrad.addColorStop(1, "#0d1a2e");
            ctx.fillStyle = avGrad;
            ctx.fillRect(cx - ar, cy - ar, ar * 2, ar * 2);
            const initials = (cardData.name || "")
                .trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
            ctx.fillStyle = "#c8a03a";
            ctx.font = "600 22px Georgia, serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(initials, cx, cy + 1);
        }
        ctx.restore();

        // ── 4. Hero text ──────────────────────────────────────────────
        const tx = cx + ar + 18;

        // ctx.fillStyle = "#c8a03a";
        // ctx.font = "500 10px Arial, sans-serif";
        // ctx.textAlign = "left";
        // ctx.textBaseline = "alphabetic";
        // ctx.fillText((cardData.designation || "").toUpperCase().slice(0, 28), tx, 44);

        // const nameParts = (cardData.name || "Your Name").trim().split(" ");
        // const firstName = nameParts[0] || "";
        // const lastName = nameParts.slice(1).join(" ");
        // ctx.fillStyle = "#ffffff";
        // ctx.font = "700 28px Georgia, serif";
        // ctx.textAlign = "left";
        // ctx.textBaseline = "alphabetic";
        // const fnW = ctx.measureText(firstName + " ").width;
        // ctx.fillText(firstName + " ", tx, 76);
        // ctx.font = "300 italic 28px Georgia, serif";
        // ctx.fillStyle = "rgba(255,255,255,0.62)";
        // ctx.fillText(lastName, tx + fnW, 76);

        // ctx.fillStyle = "rgba(255,255,255,0.4)";
        // ctx.font = "300 11px Arial, sans-serif";
        // ctx.fillText((cardData.company || "").slice(0, 30), tx, 96);

        // Replace the hero text block (section 4) with this:

        // First Name + Last Name first
        const nameParts = (cardData.name || "Your Name").trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ");
        ctx.fillStyle = "#ffffff";
        ctx.font = "700 28px Georgia, serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        const fnW = ctx.measureText(firstName + " ").width;
        ctx.fillText(firstName + " ", tx, 58);
        ctx.font = "300 italic 28px Georgia, serif";
        ctx.fillStyle = "rgba(255,255,255,0.62)";
        ctx.fillText(lastName, tx + fnW, 58);

        // Designation below name
        ctx.fillStyle = "#c8a03a";
        ctx.font = "500 10px Arial, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.fillText((cardData.designation || "").toUpperCase().slice(0, 28), tx, 78);

        // Company below designation
        ctx.fillStyle = "rgba(255,255,255,0.4)";
        ctx.font = "300 11px Arial, sans-serif";
        ctx.fillText((cardData.company || "").slice(0, 30), tx, 96);

        // ── 5. Gold divider helper ────────────────────────────────────
        const drawGoldDivider = (y) => {
            const gd = ctx.createLinearGradient(0, 0, W, 0);
            gd.addColorStop(0, "rgba(200,160,58,0)");
            gd.addColorStop(0.3, "#c8a03a");
            gd.addColorStop(0.5, "#f0c96e");
            gd.addColorStop(0.7, "#c8a03a");
            gd.addColorStop(1, "rgba(200,160,58,0)");
            ctx.fillStyle = gd;
            ctx.fillRect(0, y, W, 1);
        };

        drawGoldDivider(128);

        // ── 6. Status row ─────────────────────────────────────────────
        ctx.fillStyle = "#0a0a14";
        ctx.fillRect(0, 129, W, 40);

        // Green dot
        ctx.fillStyle = "#22c55e";
        ctx.beginPath();
        ctx.arc(26, 149, 4, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#22c55e";
        ctx.font = "500 9.5px Arial, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText("ACTIVE", 38, 149);

        // Verified badge
        const vbX = W - 90, vbY = 138, vbW = 74, vbH = 22;
        ctx.fillStyle = "rgba(34,197,94,0.1)";
        rr(vbX, vbY, vbW, vbH, 11);
        ctx.fill();
        ctx.strokeStyle = "rgba(34,197,94,0.3)";
        ctx.lineWidth = 0.8;
        rr(vbX, vbY, vbW, vbH, 11);
        ctx.stroke();
        ctx.fillStyle = "#22c55e";
        ctx.font = "500 9px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("✓  VERIFIED", vbX + vbW / 2, vbY + vbH / 2);

        // drawGoldDivider(169);

        // ── 7. QR section ─────────────────────────────────────────────
        ctx.fillStyle = "#080810";
        ctx.fillRect(0, 170, W, 290);

        const qrSize = 172;
        const qrX = (W - qrSize) / 2;
        const qrY = 192;

        // Gold corner markers
        const cornerSize = 18;
        const corners = [
            { x: qrX - 14, y: qrY - 14, dx: 1, dy: 1 },
            { x: qrX + qrSize + 14, y: qrY - 14, dx: -1, dy: 1 },
            { x: qrX - 14, y: qrY + qrSize + 14, dx: 1, dy: -1 },
            { x: qrX + qrSize + 14, y: qrY + qrSize + 14, dx: -1, dy: -1 },
        ];
        ctx.strokeStyle = "#c8a03a";
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        corners.forEach(({ x, y, dx, dy }) => {
            ctx.beginPath();
            ctx.moveTo(x + dx * cornerSize, y);
            ctx.lineTo(x, y);
            ctx.lineTo(x, y + dy * cornerSize);
            ctx.stroke();
        });

        // White QR card
        ctx.fillStyle = "#ffffff";
        rr(qrX - 12, qrY - 12, qrSize + 24, qrSize + 24, 16);
        ctx.fill();
        ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

        // Scan label
        ctx.fillStyle = "rgba(255,255,255,0.3)";
        ctx.font = "500 9px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillText("SCAN TO VIEW PROFILE", W / 2, qrY + qrSize + 30);

        // URL dot + text
        // ctx.fillStyle = "#c8a03a";
        // ctx.beginPath();
        // ctx.arc(W / 2 - 56, qrY + qrSize + 44, 2.5, 0, Math.PI * 2);
        // ctx.fill();
        // ctx.fillStyle = "rgba(200,160,58,0.8)";
        // ctx.font = "400 11px Arial, sans-serif";
        // ctx.textAlign = "left";
        // ctx.fillText(`myprofilecorner.com/${id}`, W / 2 - 50, qrY + qrSize + 47);

        const urlText = `myprofilecorner.com/${id}`;
        ctx.font = "400 11px Arial, sans-serif";
        const urlTextWidth = ctx.measureText(urlText).width;
        const dotRadius = 2.5;
        const dotTextGap = 6;
        const totalUrlW = dotRadius * 2 + dotTextGap + urlTextWidth;
        const urlStartX = (W - totalUrlW) / 2;

        ctx.fillStyle = "#c8a03a";
        ctx.beginPath();
        ctx.arc(urlStartX + dotRadius, qrY + qrSize + 44, dotRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(200,160,58,0.8)";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(urlText, urlStartX + dotRadius * 2 + dotTextGap, qrY + qrSize + 44);

        drawGoldDivider(456);

        // ── 8. Social icons (no Facebook, no Save button) ─────────────
        ctx.fillStyle = "#080810";
        ctx.fillRect(0, 457, W, 110);

        const iconList = [];
        if (cardData.email) iconList.push({ type: "email", fg: "rgba(200,160,58,0.85)", bg: "rgba(200,160,58,0.08)", border: "rgba(200,160,58,0.25)" });
        if (cardData.phone) iconList.push({ type: "phone", fg: "rgba(255,255,255,0.55)", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)" });
        if (cardData.website) iconList.push({ type: "globe", fg: "rgba(255,255,255,0.55)", bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)" });
        if (cardData.socials?.linkedin) iconList.push({ type: "linkedin", fg: "#0a66c2", bg: "rgba(10,102,194,0.08)", border: "rgba(10,102,194,0.35)" });
        if (cardData.socials?.twitter) iconList.push({ type: "twitter", fg: "rgba(255,255,255,0.75)", bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.14)" });
        if (cardData.socials?.instagram) iconList.push({ type: "instagram", fg: "#e1306c", bg: "rgba(225,48,108,0.08)", border: "rgba(225,48,108,0.35)" });
        if (cardData.socials?.whatsapp) iconList.push({ type: "whatsapp", fg: "#22c55e", bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.3)" });
        // Facebook intentionally excluded

        const displayIcons = iconList.slice(0, 6);
        const iconSize = 40;
        const iconGap = 9;
        const totalIconW = displayIcons.length * iconSize + (displayIcons.length - 1) * iconGap;
        const startX = (W - totalIconW) / 2;
        const rowY = 475;

        // Replace the entire displayIcons.forEach block with this:
        displayIcons.forEach((icon, idx) => {
            const ix = startX + idx * (iconSize + iconGap);
            const iy = rowY;
            const icx = ix + iconSize / 2;
            const icy = iy + iconSize / 2;

            // Uniform dark bg with gold border
            ctx.fillStyle = "rgba(255,255,255,0.04)";
            rr(ix, iy, iconSize, iconSize, 13);
            ctx.fill();
            ctx.strokeStyle = "rgba(200,160,58,0.2)";
            ctx.lineWidth = 0.8;
            rr(ix, iy, iconSize, iconSize, 13);
            ctx.stroke();

            // All icons in same soft white/gold
            ctx.strokeStyle = "rgba(255,255,255,0.65)";
            ctx.fillStyle = "rgba(255,255,255,0.65)";
            ctx.lineWidth = 1.6;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            const s = 9;

            ctx.save();
            if (icon.type === "email") {
                ctx.beginPath();
                ctx.strokeRect(icx - s, icy - s * 0.7, s * 2, s * 1.4);
                ctx.beginPath();
                ctx.moveTo(icx - s, icy - s * 0.7);
                ctx.lineTo(icx, icy + s * 0.1);
                ctx.lineTo(icx + s, icy - s * 0.7);
                ctx.stroke();
            } else if (icon.type === "phone") {
                ctx.font = `${s * 1.8}px Arial`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("✆", icx, icy + 1);
            } else if (icon.type === "globe") {
                ctx.beginPath();
                ctx.arc(icx, icy, s, 0, Math.PI * 2);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(icx - s, icy);
                ctx.lineTo(icx + s, icy);
                ctx.stroke();
                ctx.beginPath();
                ctx.ellipse(icx, icy, s * 0.4, s, 0, 0, Math.PI * 2);
                ctx.stroke();
            } else if (icon.type === "linkedin") {
                ctx.font = `bold ${s * 1.5}px Arial`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("in", icx, icy);
            } else if (icon.type === "twitter") {
                ctx.lineWidth = 1.8;
                ctx.beginPath();
                ctx.moveTo(icx - s * 0.7, icy - s * 0.7);
                ctx.lineTo(icx + s * 0.7, icy + s * 0.7);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(icx + s * 0.7, icy - s * 0.7);
                ctx.lineTo(icx - s * 0.7, icy + s * 0.7);
                ctx.stroke();
            } else if (icon.type === "instagram") {
                ctx.beginPath();
                rr(icx - s, icy - s, s * 2, s * 2, s * 0.42);
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(icx, icy, s * 0.48, 0, Math.PI * 2);
                ctx.stroke();
                ctx.beginPath();
                ctx.arc(icx + s * 0.6, icy - s * 0.6, 1.2, 0, Math.PI * 2);
                ctx.fill();
            } else if (icon.type === "whatsapp") {
                ctx.beginPath();
                ctx.arc(icx, icy - 1, s, 0, Math.PI * 2);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(icx - s * 0.3, icy + s * 0.6);
                ctx.lineTo(icx - s * 0.7, icy + s + 2);
                ctx.lineTo(icx + s * 0.1, icy + s * 0.72);
                ctx.stroke();
            }
            ctx.restore();
        });

        // ── 9. Footer ─────────────────────────────────────────────────
        const footY = 564;

        const fGrad = ctx.createLinearGradient(0, 0, W, 0);
        fGrad.addColorStop(0, "rgba(200,160,58,0)");
        fGrad.addColorStop(0.3, "rgba(200,160,58,0.35)");
        fGrad.addColorStop(0.7, "rgba(200,160,58,0.35)");
        fGrad.addColorStop(1, "rgba(200,160,58,0)");
        ctx.fillStyle = fGrad;
        ctx.fillRect(0, footY, W, 0.5);

        // Brand name centered with dots
        ctx.font = "600 11px Arial, sans-serif";
        const part1 = "MyProfile";
        const part2 = "Corner";
        const p1W = ctx.measureText(part1).width;
        const p2W = ctx.measureText(part2).width;
        const dotsW = 4 * 7 + 3 * 4;
        const totalBrandW = p1W + p2W + 10 + dotsW;
        const brandStartX = (W - totalBrandW) / 2;

        ctx.fillStyle = "rgba(255,255,255,0.38)";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(part1, brandStartX, footY + 28);
        ctx.fillStyle = "#c8a03a";
        ctx.fillText(part2, brandStartX + p1W, footY + 28);

        const dotColors = ["#4285f4", "#ea4335", "#fbbc04", "#34a853"];
        const dotStartX = brandStartX + p1W + p2W + 10;
        dotColors.forEach((c, i) => {
            ctx.fillStyle = c;
            ctx.beginPath();
            ctx.arc(dotStartX + i * 11 + 3.5, footY + 28, 3.5, 0, Math.PI * 2);
            ctx.fill();
        });

        // ── 10. Export PNG ────────────────────────────────────────────
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(cardData.name || "ecard").replace(/\s+/g, "_")}-ecard.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, "image/png");
    };

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.loaderWrap}>
                    <div style={styles.loaderDot} />
                    <span style={styles.loaderText}>Loading card…</span>
                </div>
            </div>
        );
    }

    if (!cardData) {
        return (
            <div style={styles.page}>
                <div style={styles.notFound}>
                    <div style={styles.notFoundIcon}>🪪</div>
                    <h2 style={styles.notFoundTitle}>Card not found</h2>
                    <p style={styles.notFoundSub}>This digital card does not exist.</p>
                    <a href="/" style={styles.backLink}>Create one →</a>
                </div>
            </div>
        );
    }

    // Only the card owner can view this page
    if (!user) {
        return (
            <div style={styles.page}>
                <div style={styles.notFound}>
                    <div style={styles.notFoundIcon}>🔒</div>
                    <h2 style={styles.notFoundTitle}>Sign in required</h2>
                    <p style={styles.notFoundSub}>Please sign in to view this card.</p>
                    <a href="/" style={styles.backLink}>Go to sign in →</a>
                </div>
            </div>
        );
    }

    if (cardOwnerId && user.uid !== cardOwnerId) {
        return (
            <div style={styles.page}>
                <div style={styles.notFound}>
                    <div style={styles.notFoundIcon}>🚫</div>
                    <h2 style={styles.notFoundTitle}>Access denied</h2>
                    <p style={styles.notFoundSub}>You don't have permission to view this card.</p>
                    <a href="/" style={styles.backLink}>Go home →</a>
                </div>
            </div>
        );
    }

    const isOwner = user && cardOwnerId && user.uid === cardOwnerId;

    return (
        <>
            <div style={styles.page}>
                <div style={styles.container}>

                    {/* Owner bar */}
                    {isOwner && (
                        <div style={styles.ownerBar}>
                            <div style={styles.ownerLeft}>
                                {user.photoURL
                                    ? <img src={user.photoURL} alt="avatar" style={styles.ownerAvatar} />
                                    : <div style={styles.ownerAvatarFallback}>{user.displayName?.[0] || "U"}</div>
                                }
                                <span style={styles.ownerName}>
                                    Signed in as {user.displayName?.split(" ")[0]}
                                </span>
                            </div>
                            <button onClick={logout} style={styles.logoutBtn}>Sign out</button>
                        </div>
                    )}

                    {/* Full Card Preview */}
                    <style>{CARD_FONTS}</style>
                    <CardRenderer
                        themeId={cardData.theme || "dark"}
                        cardData={cardData}
                        onSave={downloadVCard}
                        saved={false}
                        onVideoClick={cardData.promoVideoUrl ? () => setShowVideoModal(true) : undefined}
                    />

                    {/* Actions Panel */}
                    <div style={styles.actionsPanel}>
                        <div style={styles.actionGrid}>

                            {/* Edit Card tile */}
                            <button onClick={() => navigate(`/edit/${id}`)} style={styles.tileEdit}>
                                <div style={styles.tileDecoCircle1} />
                                <div style={styles.tileIconBoxEdit}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6c47ff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </div>
                                <div style={styles.tileTextWrap}>
                                    <span style={{ ...styles.tileLabel, color: "#6c47ff" }}>Edit Card</span>
                                    <span style={{ ...styles.tileSub, color: "#9b7ee8" }}>Manage info</span>
                                </div>
                            </button>

                            {/* View Profile tile */}
                            <button onClick={() => window.open(cardUrl, "_blank")} style={styles.tileView}>
                                <div style={styles.tileDecoCircle2} />
                                <div style={styles.tileIconBoxView}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0AADA1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                        <polyline points="15 3 21 3 21 9" />
                                        <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                </div>
                                <div style={styles.tileTextWrap}>
                                    <span style={{ ...styles.tileLabel, color: "#0AADA1" }}>View Profile</span>
                                    <span style={{ ...styles.tileSub, color: "#50c8bf" }}>Public page</span>
                                </div>
                            </button>

                        </div>

                        {/* Hidden QR canvas used for PNG e-card generation */}
                        <div ref={qrRef} style={{ position: "absolute", left: -9999, top: -9999, pointerEvents: "none" }}>
                            {cardData && <QRCodeCanvas value={cardUrl} size={200} />}
                        </div>

                        {!walletLoading && walletUrl && (
                            <a href={walletUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block" }}>
                                <button style={styles.btnWallet}>
                                    <span style={styles.walletDot} />
                                    <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                        <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                        <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                    </svg>
                                    Add to Google Wallet
                                </button>
                            </a>
                        )}

                        {/* Download E-Card PNG */}
                        <button onClick={downloadECard} style={styles.btnDownload}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download Your E-Card
                        </button>
                    </div>

                    {/* ── Share Row ── */}
                    <div style={styles.sharePanel}>
                        <div style={styles.socialRow}>
                            {/* Copy */}
                            <button onClick={copyLink} title="Copy link" style={{ ...styles.socialCircle, background: copied ? "linear-gradient(135deg,#22c55e,#16a34a)" : "linear-gradient(135deg,#0AADA1,#0891b2)", border: "none", cursor: "pointer", boxShadow: copied ? "0 3px 10px rgba(34,197,94,0.3)" : "0 3px 10px rgba(10,173,161,0.25)", transition: "all 0.25s" }}>
                                {copied
                                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                                }
                            </button>

                            {/* WhatsApp */}
                            <a href={`https://wa.me/?text=${encodeURIComponent("Check out my digital card: " + cardUrl)}`} target="_blank" rel="noopener noreferrer" style={{ ...styles.socialCircle, background: "linear-gradient(135deg,#25D366,#128C7E)" }} title="WhatsApp">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.558 4.112 1.528 5.837L0 24l6.335-1.652A11.954 11.954 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.374l-.36-.213-3.718.969.99-3.61-.234-.374A9.792 9.792 0 0 1 2.182 12C2.182 6.58 6.58 2.182 12 2.182S21.818 6.58 21.818 12 17.42 21.818 12 21.818z" /></svg>
                            </a>

                            {/* LinkedIn */}
                            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(cardUrl)}`} target="_blank" rel="noopener noreferrer" style={{ ...styles.socialCircle, background: "linear-gradient(135deg,#0A66C2,#004182)" }} title="LinkedIn">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                            </a>

                            {/* Twitter / X */}
                            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Check out my digital card")}&url=${encodeURIComponent(cardUrl)}`} target="_blank" rel="noopener noreferrer" style={{ ...styles.socialCircle, background: "linear-gradient(135deg,#1a1a1a,#333)" }} title="X / Twitter">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="#fff"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                            </a>

                            {/* Telegram */}
                            <a href={`https://t.me/share/url?url=${encodeURIComponent(cardUrl)}&text=${encodeURIComponent("Check out my digital card")}`} target="_blank" rel="noopener noreferrer" style={{ ...styles.socialCircle, background: "linear-gradient(135deg,#229ED9,#1a7ab5)" }} title="Telegram">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
                            </a>
                        </div>
                    </div>

                    {/* ── Upgrade Banner (non-premium owners only) ── */}
                    {!isPremium && (
                        <button onClick={() => setShowUpgrade(true)} style={styles.upgradeBanner}>
                            <div style={styles.upgradeLeft}>
                                <span style={styles.upgradeEmoji}>✨</span>
                                <div>
                                    <div style={styles.upgradeTitle}>Unlock Premium Themes</div>
                                    <div style={styles.upgradeSub}>200+ themes · Custom fonts · ₹99/mo</div>
                                </div>
                            </div>
                            <span style={styles.upgradeArrow}>Upgrade →</span>
                        </button>
                    )}

                    {isPremium && (
                        <div style={styles.premiumBadge}>
                            <span>✨</span> Premium Active
                        </div>
                    )}

                </div>
            </div>

            {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}

            {showVideoModal && cardData?.promoVideoUrl && (
                <ProfileVideoModal
                    url={cardData.promoVideoUrl}
                    type={cardData.promoVideoType || "youtube"}
                    onClose={() => setShowVideoModal(false)}
                />
            )}
        </>
    );
}

function getYoutubeId(url) {
    const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return m ? m[1] : null;
}

function ProfileVideoModal({ url, type, onClose }) {
    const ytId = type === "youtube" ? getYoutubeId(url) : null;
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            onClick={onClose}
        >
            <button
                onClick={onClose}
                style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >×</button>
            <div style={{ width: "100%", maxWidth: "600px" }} onClick={e => e.stopPropagation()}>
                {ytId ? (
                    <div style={{ position: "relative", paddingBottom: "56.25%", borderRadius: "16px", overflow: "hidden", background: "#000" }}>
                        <iframe
                            src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                            allow="autoplay; encrypted-media; fullscreen"
                            allowFullScreen
                            title="Promo Video"
                        />
                    </div>
                ) : (
                    <video
                        src={url}
                        controls
                        autoPlay
                        style={{ width: "100%", borderRadius: "16px", maxHeight: "80vh", background: "#000" }}
                    />
                )}
            </div>
        </div>
    );
}

const styles = {
    /* Page shell */
    page: {
        minHeight: "100vh",
        background: "#f0f2f5",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "2rem 1rem 3rem",
        fontFamily: "'Montserrat', 'Segoe UI', system-ui, sans-serif",
    },
    container: { width: "100%", maxWidth: "400px" },

    /* Loader */
    loaderWrap: {
        display: "flex", alignItems: "center", gap: "10px",
        marginTop: "5rem", fontSize: "14px",
    },
    loaderDot: {
        width: "8px", height: "8px", borderRadius: "50%",
        background: "#9ca3af",
    },
    loaderText: { color: "#9ca3af", fontSize: "14px" },

    /* Not found */
    notFound: {
        background: "#fff", borderRadius: "20px",
        padding: "3rem 2rem", textAlign: "center",
    },
    notFoundIcon: { fontSize: "2.5rem", marginBottom: "1rem" },
    notFoundTitle: { fontSize: "1.3rem", fontWeight: "600", color: "#111", marginBottom: "0.5rem" },
    notFoundSub: { fontSize: "14px", color: "#9ca3af", marginBottom: "1.5rem" },
    backLink: {
        color: "#111", fontWeight: "600", fontSize: "14px",
        textDecoration: "none", borderBottom: "1.5px solid #111", paddingBottom: "1px",
    },

    /* Owner bar */
    ownerBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "14px", padding: "8px 14px", marginBottom: "12px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    ownerLeft: { display: "flex", alignItems: "center", gap: "8px" },
    ownerAvatar: { width: "26px", height: "26px", borderRadius: "50%", objectFit: "cover" },
    ownerAvatarFallback: {
        width: "26px", height: "26px", borderRadius: "50%",
        background: "#6366f1", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "11px", fontWeight: "500",
    },
    ownerName: { fontSize: "12px", color: "#6b7280", fontWeight: "500" },
    logoutBtn: {
        background: "#f4f5f7",
        border: "1px solid #e5e7eb",
        color: "#6b7280",
        borderRadius: "8px", padding: "4px 10px",
        fontSize: "11px", fontWeight: "500", cursor: "pointer",
    },

    /* Actions panel */
    actionsPanel: {
        marginTop: "10px",
        background: "#fff",
        borderRadius: "22px",
        border: "1px solid #ebebeb",
        padding: "10px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    },
    actionGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "8px",
        marginBottom: "8px",
    },

    /* Edit tile — lavender */
    tileEdit: {
        position: "relative", overflow: "hidden",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "10px 12px", height: "74px",
        background: "#ede9ff",
        border: "none", borderRadius: "16px", cursor: "pointer", textAlign: "left",
    },
    /* View tile — mint */
    tileView: {
        position: "relative", overflow: "hidden",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: "10px 12px", height: "74px",
        background: "#e6faf3",
        border: "none", borderRadius: "16px", cursor: "pointer", textAlign: "left",
    },
    tileDecoCircle1: {
        position: "absolute", top: "-16px", right: "-16px",
        width: "70px", height: "70px", borderRadius: "50%",
        background: "rgba(180,150,255,0.35)",
        pointerEvents: "none",
    },
    tileDecoCircle2: {
        position: "absolute", top: "-16px", right: "-16px",
        width: "70px", height: "70px", borderRadius: "50%",
        background: "rgba(10,173,161,0.2)",
        pointerEvents: "none",
    },
    tileIconBoxEdit: {
        width: "30px", height: "30px", borderRadius: "8px",
        background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 1px 4px rgba(108,71,255,0.15)",
    },
    tileIconBoxView: {
        width: "30px", height: "30px", borderRadius: "8px",
        background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 1px 4px rgba(10,173,161,0.15)",
    },
    tileTextWrap: {
        display: "flex", flexDirection: "column", gap: "2px",
    },
    tileLabel: {
        fontSize: "14px", fontWeight: "800",
        letterSpacing: "-0.3px", display: "block",
    },
    tileSub: {
        fontSize: "11px", display: "block",
    },

    /* Wallet */
    btnWallet: {
        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        width: "100%", padding: "15px",
        background: "#141414",
        color: "#fff", border: "none", borderRadius: "16px",
        fontSize: "14px", fontWeight: "700", cursor: "pointer",
        letterSpacing: "-0.2px",
    },
    walletDot: {
        width: "8px", height: "8px", borderRadius: "50%",
        background: "#22c55e", flexShrink: 0,
    },
    btnDownload: {
        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        width: "100%", padding: "15px", marginTop: "8px",
        background: "linear-gradient(135deg, #0AADA1, #0f8f85)",
        color: "#fff", border: "none", borderRadius: "16px",
        fontSize: "14px", fontWeight: "700", cursor: "pointer",
        letterSpacing: "-0.2px",
    },

    /* Share section */
    sharePanel: {
        marginTop: "10px",
        background: "#fff",
        borderRadius: "16px",
        border: "1px solid #ebebeb",
        padding: "10px 14px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        justifyContent: "center",
    },
    shareLabel: {
        textAlign: "center", fontSize: "10px", fontWeight: "700",
        letterSpacing: "2px", textTransform: "uppercase",
        color: "#9ca3af", margin: "0 0 12px",
    },
    copyBtn: {
        width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        padding: "14px", borderRadius: "14px", border: "none", cursor: "pointer",
        color: "#fff", fontWeight: "700", fontSize: "14px",
        marginBottom: "12px", transition: "all 0.25s", letterSpacing: "-0.2px",
    },
    socialRow: {
        display: "flex", gap: "7px", justifyContent: "center",
    },
    socialCircle: {
        width: "32px", height: "32px", borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        textDecoration: "none", boxShadow: "0 2px 6px rgba(0,0,0,0.13)",
        transition: "transform 0.2s",
    },
    themePanel: {
        padding: "16px 14px 14px",
        borderTop: "0.5px solid #f0f1f3",
        background: "#fff",
    },
    themeHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
    },
    themeTitle: {
        fontSize: "11px",
        fontWeight: "700",
        color: "#6b7280",
        letterSpacing: "1px",
        textTransform: "uppercase",
    },
    themeLinkBtn: {
        background: "none",
        border: "none",
        padding: 0,
        color: "#6366f1",
        fontSize: "12px",
        fontWeight: "700",
        cursor: "pointer",
    },
    themePreviewRow: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "10px",
    },
    themePreviewBtn: {
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "8px 6px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
    },
    themePreviewThumb: {
        width: "100%",
        maxWidth: "86px",
        height: "54px",
        borderRadius: "10px",
        position: "relative",
        overflow: "hidden",
    },
    themePreviewAvatar: {
        position: "absolute",
        left: "6px",
        bottom: "6px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        border: "1.5px solid #fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "7px",
        fontWeight: "800",
    },
    themePreviewLabel: {
        fontSize: "10px",
        fontWeight: "700",
        color: "#374151",
        lineHeight: 1.2,
        textAlign: "center",
    },
    themeMoreBtn: {
        background: "linear-gradient(135deg, #111827, #1f2937)",
        border: "1px solid #111827",
        borderRadius: "12px",
        padding: "8px 6px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        cursor: "pointer",
        color: "#fff",
    },
    themeMoreCount: {
        fontSize: "18px",
        fontWeight: "800",
        lineHeight: 1,
    },
    themeMoreText: {
        fontSize: "10px",
        fontWeight: "600",
        color: "rgba(255,255,255,0.75)",
        textAlign: "center",
        lineHeight: 1.2,
    },

    /* Upgrade banner */
    upgradeBanner: {
        marginTop: "10px",
        width: "100%",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(135deg, #0D0B1A, #1A1535)",
        border: "1px solid rgba(10,173,161,0.25)",
        borderRadius: "16px",
        padding: "14px 16px",
        cursor: "pointer",
        boxShadow: "0 4px 16px rgba(10,173,161,0.12)",
        boxSizing: "border-box",
    },
    upgradeLeft: { display: "flex", alignItems: "center", gap: "12px" },
    upgradeEmoji: { fontSize: "22px", lineHeight: 1 },
    upgradeTitle: { fontSize: "13px", fontWeight: "700", color: "#fff", marginBottom: "2px", textAlign: "left" },
    upgradeSub: { fontSize: "11px", color: "rgba(255,255,255,0.45)", textAlign: "left" },
    upgradeArrow: { fontSize: "12px", fontWeight: "700", color: "#0AADA1", whiteSpace: "nowrap" },

    /* Premium badge */
    premiumBadge: {
        marginTop: "10px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
        background: "linear-gradient(135deg, rgba(10,173,161,0.1), rgba(10,173,161,0.05))",
        border: "1px solid rgba(10,173,161,0.25)",
        borderRadius: "12px",
        padding: "8px 14px",
        fontSize: "12px", fontWeight: "700", color: "#0AADA1",
    },

    /* Footer */
    panelFooter: {
        padding: "10px",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "#f9fafb", borderTop: "0.5px solid #f0f1f3",
    },
    footerText: { fontSize: "11px", color: "#c0c4cc", letterSpacing: "0.3px" },
};

export default ProfilePage;

// function ProfilePage() {
//     const { id } = useParams();
//     const [cardData, setCardData] = useState(null);
//     const [loading, setLoading] = useState(true);
//     const [walletUrl, setWalletUrl] = useState(null);
//     const [walletLoading, setWalletLoading] = useState(false);
//     const [copied, setCopied] = useState(false);
//     const [tab, setTab] = useState("card");
//     const navigate = useNavigate();
//     const { user, logout } = useAuth();
//     const [cardOwnerId, setCardOwnerId] = useState(null);

//     const baseUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
//     const cardUrl = `${baseUrl}/${id}`;

//     useEffect(() => {
//         const fetchCard = async () => {
//             try {
//                 let snapshot = await getDocs(
//                     query(collection(db, "cards"), where("slug", "==", id))
//                 );
//                 if (snapshot.empty) {
//                     snapshot = await getDocs(
//                         query(collection(db, "cards"), where("phone", "==", id))
//                     );
//                 }
//                 if (!snapshot.empty) {
//                     const doc = snapshot.docs[0].data();
//                     setCardData(doc);
//                     setCardOwnerId(doc.userId || null);
//                     fetchWalletUrl(doc, cardUrl);
//                 } else {
//                     setCardData(null);
//                 }
//             } catch (err) {
//                 console.error("Error fetching card:", err);
//                 setCardData(null);
//             } finally {
//                 setLoading(false);
//             }
//         };
//         fetchCard();
//     }, [id]);

//     const fetchWalletUrl = async (doc, url) => {
//         setWalletLoading(true);
//         try {
//             const res = await fetch(`${WALLET_BACKEND}/generate-pass`, {
//                 method: "POST",
//                 headers: { "Content-Type": "application/json" },
//                 body: JSON.stringify({
//                     name: doc.name,
//                     phone: doc.phone,
//                     email: doc.email,
//                     cardUrl: url,
//                     photoUrl: doc.photoUrl || "",
//                     designation: doc.designation || "",
//                     company: doc.company || "",
//                     location: doc.location || "",
//                     website: doc.website || "",
//                     resumeUrl: doc.resumeUrl || "",
//                     socials: doc.socials || {},
//                 }),
//             });
//             const data = await res.json();
//             setWalletUrl(data.url);
//         } catch (err) {
//             console.warn("Wallet backend unavailable:", err.message);
//         } finally {
//             setWalletLoading(false);
//         }
//     };

//     const copyLink = () => {
//         navigator.clipboard.writeText(cardUrl).then(() => {
//             setCopied(true);
//             setTimeout(() => setCopied(false), 2500);
//         });
//     };

//     const isOwner = user && cardOwnerId && user.uid === cardOwnerId;

//     if (loading) {
//         return (
//             <div style={s.loadPage}>
//                 <div style={s.spinner} />
//                 <p style={s.loadText}>Loading card...</p>
//             </div>
//         );
//     }

//     if (!cardData) {
//         return (
//             <div style={s.loadPage}>
//                 <div style={s.notFound}>
//                     <div style={s.notFoundIcon}>🪪</div>
//                     <h2 style={s.notFoundTitle}>Card not found</h2>
//                     <p style={s.notFoundSub}>This digital card does not exist.</p>
//                     <a href="/" style={s.notFoundLink}>Create your card →</a>
//                 </div>
//             </div>
//         );
//     }

//     const initials = cardData.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
//     const enabledSocials = cardData.socials
//         ? SOCIAL_PLATFORMS.filter(p => cardData.socials[p.key])
//         : [];

//     return (
//         <div style={s.page}>

//             {/* Top nav bar */}
//             <header style={s.navbar}>
//                 <div style={s.navBrand}>
//                     <span style={s.navLogo}>⬡</span>
//                     <span style={s.navTitle}>MyProfileCorner</span>
//                 </div>
//                 {isOwner ? (
//                     <div style={s.navRight}>
//                         <div style={s.navUser}>
//                             {user.photoURL
//                                 ? <img src={user.photoURL} alt="avatar" style={s.navAvatar} />
//                                 : <div style={s.navAvatarFallback}>{user.displayName?.[0] || "U"}</div>
//                             }
//                             <span style={s.navUserName}>{user.displayName?.split(" ")[0]}</span>
//                         </div>
//                         <button onClick={() => navigate(`/edit/${id}`)} style={s.navEditBtn}>Edit Card</button>
//                         <button onClick={logout} style={s.navLogoutBtn}>Sign out</button>
//                     </div>
//                 ) : (
//                     <a href="/" style={s.navCta}>Create yours →</a>
//                 )}
//             </header>

//             {/* Hero section */}
//             <div style={s.hero}>
//                 <div style={s.heroInner}>
//                     {cardData.photoUrl
//                         ? <img src={cardData.photoUrl} alt={cardData.name} style={s.heroPhoto} />
//                         : <div style={s.heroInitials}>{initials}</div>
//                     }
//                     <h1 style={s.heroName}>{cardData.name}</h1>
//                     {(cardData.designation || cardData.company) && (
//                         <p style={s.heroRole}>
//                             {[cardData.designation, cardData.company].filter(Boolean).join("  ·  ")}
//                         </p>
//                     )}
//                     {cardData.location && (
//                         <p style={s.heroLocation}>📍 {cardData.location}</p>
//                     )}
//                     {enabledSocials.length > 0 && (
//                         <div style={s.heroSocials}>
//                             {enabledSocials.map(p => (
//                                 <a
//                                     key={p.key}
//                                     href={cardData.socials[p.key]}
//                                     target="_blank"
//                                     rel="noopener noreferrer"
//                                     style={{ ...s.socialChip, background: p.color }}
//                                     title={p.label}
//                                 >
//                                     {p.icon}
//                                 </a>
//                             ))}
//                         </div>
//                     )}
//                 </div>
//             </div>

//             {/* Tab bar */}
//             <div style={s.tabWrap}>
//                 <div style={s.tabBar}>
//                     <button
//                         onClick={() => setTab("card")}
//                         style={{ ...s.tabBtn, ...(tab === "card" ? s.tabActive : {}) }}
//                     >
//                         Card Overview
//                     </button>
//                     <button
//                         onClick={() => setTab("preview")}
//                         style={{ ...s.tabBtn, ...(tab === "preview" ? s.tabActive : {}) }}
//                     >
//                         Full Preview
//                     </button>
//                 </div>
//             </div>

//             {/* Main content */}
//             <main style={s.main}>

//                 {tab === "card" && (
//                     <div style={s.grid}>

//                         {/* Left column */}
//                         <div style={s.leftCol}>

//                             {/* Contact */}
//                             <section style={s.card}>
//                                 <h2 style={s.sectionTitle}>Contact</h2>
//                                 <div style={s.contactList}>
//                                     <a href={`tel:${cardData.phone}`} style={s.contactItem}>
//                                         <div style={s.contactIconWrap}>📞</div>
//                                         <div>
//                                             <div style={s.contactLabel}>Phone</div>
//                                             <div style={s.contactValue}>{cardData.phone}</div>
//                                         </div>
//                                     </a>
//                                     <a href={`mailto:${cardData.email}`} style={s.contactItem}>
//                                         <div style={s.contactIconWrap}>✉️</div>
//                                         <div>
//                                             <div style={s.contactLabel}>Email</div>
//                                             <div style={s.contactValue}>{cardData.email}</div>
//                                         </div>
//                                     </a>
//                                     {cardData.website && (
//                                         <a href={cardData.website} target="_blank" rel="noopener noreferrer" style={s.contactItem}>
//                                             <div style={s.contactIconWrap}>🌐</div>
//                                             <div>
//                                                 <div style={s.contactLabel}>Website</div>
//                                                 <div style={s.contactValue}>{cardData.website.replace(/^https?:\/\//, "")}</div>
//                                             </div>
//                                         </a>
//                                     )}
//                                 </div>
//                             </section>

//                             {/* Bio */}
//                             {cardData.bio && (
//                                 <section style={s.card}>
//                                     <h2 style={s.sectionTitle}>About</h2>
//                                     <p style={s.bioText}>{cardData.bio}</p>
//                                 </section>
//                             )}

//                             {/* Resume */}
//                             {cardData.resumeUrl && (
//                                 <section style={s.card}>
//                                     <h2 style={s.sectionTitle}>Resume</h2>
//                                     <a href={cardData.resumeUrl} target="_blank" rel="noopener noreferrer" style={s.resumeBtn}>
//                                         📄 View Resume
//                                     </a>
//                                 </section>
//                             )}
//                         </div>

//                         {/* Right column */}
//                         <div style={s.rightCol}>

//                             {/* QR Code */}
//                             <section style={s.card}>
//                                 <h2 style={s.sectionTitle}>Share This Card</h2>
//                                 <div style={s.qrWrap}>
//                                     <div style={s.qrBox}>
//                                         <QRCodeCanvas value={cardUrl} size={148} />
//                                     </div>
//                                     <p style={s.qrHint}>Scan QR code to open this card</p>
//                                     <button onClick={copyLink} style={s.copyBtn}>
//                                         {copied ? "✓ Link Copied!" : "🔗 Copy Card Link"}
//                                     </button>
//                                 </div>
//                             </section>

//                             {/* Actions */}
//                             <section style={s.card}>
//                                 <h2 style={s.sectionTitle}>Actions</h2>
//                                 <div style={s.actionList}>
//                                     <a href={cardUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
//                                         <button style={s.actionBtn}>🔗 Open Public Card</button>
//                                     </a>
//                                     {isOwner && (
//                                         <button onClick={() => navigate(`/edit/${id}`)} style={{ ...s.actionBtn, ...s.actionBtnEdit }}>
//                                             ✏️ Edit My Card
//                                         </button>
//                                     )}
//                                     {walletLoading && (
//                                         <button style={{ ...s.actionBtn, ...s.actionBtnWallet, opacity: 0.6 }} disabled>
//                                             Loading Wallet...
//                                         </button>
//                                     )}
//                                     {!walletLoading && walletUrl && (
//                                         <a href={walletUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
//                                             <button style={{ ...s.actionBtn, ...s.actionBtnWallet }}>
//                                                 🪙 Add to Google Wallet
//                                             </button>
//                                         </a>
//                                     )}
//                                 </div>
//                             </section>

//                         </div>
//                     </div>
//                 )}

//                 {tab === "preview" && (
//                     <div style={s.previewWrap}>
//                         <FullCardPreview
//                             name={cardData.name}
//                             phone={cardData.phone}
//                             email={cardData.email}
//                             location={cardData.location}
//                             designation={cardData.designation}
//                             company={cardData.company}
//                             bio={cardData.bio}
//                             photoUrl={cardData.photoUrl}
//                             website={cardData.website}
//                             resumeUrl={cardData.resumeUrl}
//                             enabledSocials={enabledSocials}
//                         />
//                         <div style={s.previewActions}>
//                             {isOwner && (
//                                 <button onClick={() => navigate(`/edit/${id}`)} style={{ ...s.actionBtn, ...s.actionBtnEdit }}>
//                                     ✏️ Edit My Card
//                                 </button>
//                             )}
//                             <a href={cardUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
//                                 <button style={s.actionBtn}>🔗 Open Public Card</button>
//                             </a>
//                         </div>
//                     </div>
//                 )}

//             </main>

//             <footer style={s.footer}>
//                 <span>© 2025 MyProfileCorner</span>
//                 <a href="/" style={s.footerLink}>Create your free card →</a>
//             </footer>
//         </div>
//     );
// }

// const s = {
//     /* Loading / error states */
//     loadPage: {
//         minHeight: "100vh",
//         background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 60%, #0f3460 100%)",
//         display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
//         gap: "1rem", fontFamily: "'Segoe UI', Arial, sans-serif",
//     },
//     spinner: {
//         width: "40px", height: "40px", borderRadius: "50%",
//         border: "3px solid rgba(255,255,255,0.15)",
//         borderTop: "3px solid #6366f1",
//         animation: "spin 0.8s linear infinite",
//     },
//     loadText: { color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" },
//     notFound: {
//         background: "#fff", borderRadius: "20px",
//         padding: "3rem 2.5rem", textAlign: "center",
//         boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
//     },
//     notFoundIcon: { fontSize: "3rem", marginBottom: "0.75rem" },
//     notFoundTitle: { fontSize: "1.4rem", fontWeight: "800", color: "#1a1a2e", margin: "0 0 0.5rem" },
//     notFoundSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 1.5rem" },
//     notFoundLink: { color: "#6366f1", fontWeight: "700", textDecoration: "none", fontSize: "0.95rem" },

//     /* Page shell */
//     page: {
//         minHeight: "100vh",
//         background: "#f4f5f7",
//         fontFamily: "'Segoe UI', Arial, sans-serif",
//         display: "flex", flexDirection: "column",
//     },

//     /* Navbar */
//     navbar: {
//         background: "#0f1923",
//         padding: "0 2rem",
//         height: "60px",
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//         boxShadow: "0 2px 16px rgba(0,0,0,0.4)",
//         position: "sticky", top: 0, zIndex: 100,
//     },
//     navBrand: { display: "flex", alignItems: "center", gap: "0.5rem" },
//     navLogo: { fontSize: "1.3rem", color: "#6366f1" },
//     navTitle: { fontSize: "1rem", fontWeight: "800", color: "#fff", letterSpacing: "-0.3px" },
//     navRight: { display: "flex", alignItems: "center", gap: "0.75rem" },
//     navUser: { display: "flex", alignItems: "center", gap: "0.5rem" },
//     navAvatar: { width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover" },
//     navAvatarFallback: {
//         width: "28px", height: "28px", borderRadius: "50%",
//         background: "#6366f1", color: "#fff",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         fontSize: "0.75rem", fontWeight: "700",
//     },
//     navUserName: { fontSize: "0.82rem", color: "rgba(255,255,255,0.7)", fontWeight: "600" },
//     navEditBtn: {
//         background: "#6366f1", color: "#fff", border: "none",
//         borderRadius: "8px", padding: "0.35rem 0.9rem",
//         fontSize: "0.8rem", fontWeight: "700", cursor: "pointer",
//     },
//     navLogoutBtn: {
//         background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)",
//         border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px",
//         padding: "0.35rem 0.9rem", fontSize: "0.8rem", fontWeight: "600", cursor: "pointer",
//     },
//     navCta: {
//         fontSize: "0.85rem", fontWeight: "700", color: "#6366f1",
//         textDecoration: "none",
//     },

//     /* Hero */
//     hero: {
//         background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
//         padding: "3rem 1rem 3.5rem",
//         textAlign: "center",
//         color: "#fff",
//     },
//     heroInner: { maxWidth: "680px", margin: "0 auto" },
//     heroPhoto: {
//         width: "100px", height: "100px", borderRadius: "50%",
//         objectFit: "cover",
//         border: "4px solid rgba(255,255,255,0.25)",
//         boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
//         display: "block", margin: "0 auto 1.25rem",
//     },
//     heroInitials: {
//         width: "96px", height: "96px", borderRadius: "50%",
//         background: "rgba(255,255,255,0.12)",
//         border: "4px solid rgba(255,255,255,0.25)",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         fontSize: "2rem", fontWeight: "800",
//         margin: "0 auto 1.25rem",
//         boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
//     },
//     heroName: {
//         margin: "0 0 0.4rem",
//         fontSize: "2.2rem", fontWeight: "900",
//         letterSpacing: "-0.8px",
//         textShadow: "0 2px 8px rgba(0,0,0,0.3)",
//     },
//     heroRole: {
//         margin: "0 0 0.5rem",
//         fontSize: "1rem", opacity: 0.75, fontWeight: "500",
//     },
//     heroLocation: {
//         margin: "0 0 1.25rem",
//         fontSize: "0.85rem", opacity: 0.6,
//     },
//     heroSocials: {
//         display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap",
//     },
//     socialChip: {
//         display: "inline-flex", alignItems: "center", justifyContent: "center",
//         width: "36px", height: "36px", borderRadius: "10px",
//         color: "#fff", fontSize: "0.75rem", fontWeight: "800",
//         textDecoration: "none",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
//         transition: "transform 0.15s",
//     },

//     /* Tab bar */
//     tabWrap: {
//         background: "#fff",
//         borderBottom: "1px solid #e5e7eb",
//         display: "flex", justifyContent: "center",
//         position: "sticky", top: "60px", zIndex: 90,
//     },
//     tabBar: { display: "flex", gap: 0 },
//     tabBtn: {
//         padding: "0.85rem 2rem",
//         background: "transparent", border: "none",
//         borderBottom: "3px solid transparent",
//         fontSize: "0.88rem", fontWeight: "600",
//         color: "#6b7280", cursor: "pointer",
//         transition: "all 0.2s",
//     },
//     tabActive: {
//         color: "#1a1a2e",
//         borderBottom: "3px solid #6366f1",
//     },

//     /* Main */
//     main: {
//         flex: 1,
//         maxWidth: "900px", width: "100%",
//         margin: "0 auto", padding: "2rem 1rem",
//     },

//     /* Two-column grid */
//     grid: {
//         display: "grid",
//         gridTemplateColumns: "1fr 340px",
//         gap: "1.5rem",
//         alignItems: "start",
//     },
//     leftCol: { display: "flex", flexDirection: "column", gap: "1.25rem" },
//     rightCol: { display: "flex", flexDirection: "column", gap: "1.25rem" },

//     /* Card panel */
//     card: {
//         background: "#fff",
//         borderRadius: "16px",
//         padding: "1.5rem",
//         boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
//         border: "1px solid #e9eaec",
//     },
//     sectionTitle: {
//         margin: "0 0 1.1rem",
//         fontSize: "0.72rem", fontWeight: "800",
//         color: "#9ca3af", textTransform: "uppercase",
//         letterSpacing: "1.2px",
//     },

//     /* Contact */
//     contactList: { display: "flex", flexDirection: "column", gap: "0.1rem" },
//     contactItem: {
//         display: "flex", alignItems: "center", gap: "0.9rem",
//         padding: "0.65rem 0.5rem", borderRadius: "10px",
//         textDecoration: "none", color: "#1a1a2e",
//         transition: "background 0.15s",
//     },
//     contactIconWrap: {
//         fontSize: "1.1rem",
//         width: "36px", height: "36px", borderRadius: "10px",
//         background: "#f4f5f7",
//         display: "flex", alignItems: "center", justifyContent: "center",
//         flexShrink: 0,
//     },
//     contactLabel: { fontSize: "0.72rem", color: "#9ca3af", fontWeight: "600", marginBottom: "1px" },
//     contactValue: { fontSize: "0.92rem", fontWeight: "600", color: "#111827" },

//     /* Bio */
//     bioText: { margin: 0, fontSize: "0.92rem", color: "#374151", lineHeight: 1.7 },

//     /* Resume */
//     resumeBtn: {
//         display: "inline-flex", alignItems: "center", gap: "0.4rem",
//         padding: "0.65rem 1.25rem",
//         background: "#f4f5f7", color: "#374151",
//         borderRadius: "10px", textDecoration: "none",
//         fontSize: "0.88rem", fontWeight: "700",
//         border: "1.5px solid #e5e7eb",
//     },

//     /* QR */
//     qrWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.85rem" },
//     qrBox: {
//         padding: "12px", background: "#fff",
//         borderRadius: "12px", border: "1px solid #e5e7eb",
//         boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
//     },
//     qrHint: { margin: 0, fontSize: "0.78rem", color: "#9ca3af", fontWeight: "500" },
//     copyBtn: {
//         width: "100%", padding: "0.75rem",
//         background: "#f4f5f7", color: "#374151",
//         border: "1.5px solid #e5e7eb", borderRadius: "10px",
//         fontSize: "0.88rem", fontWeight: "700", cursor: "pointer",
//     },

//     /* Action buttons */
//     actionList: { display: "flex", flexDirection: "column", gap: "0.6rem" },
//     actionBtn: {
//         width: "100%", padding: "0.78rem",
//         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         color: "#fff", border: "none", borderRadius: "10px",
//         fontSize: "0.9rem", fontWeight: "700", cursor: "pointer",
//     },
//     actionBtnEdit: {
//         background: "#f4f5f7", color: "#374151",
//         border: "1.5px solid #e5e7eb",
//     },
//     actionBtnWallet: {
//         background: "#0F9D58",
//         border: "none", color: "#fff",
//     },

//     /* Full preview tab */
//     previewWrap: {
//         display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem",
//     },
//     previewActions: {
//         display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap",
//         width: "100%", maxWidth: "420px",
//     },

//     /* Footer */
//     footer: {
//         background: "#0f1923",
//         padding: "1rem 2rem",
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//         fontSize: "0.78rem", color: "rgba(255,255,255,0.4)",
//     },
//     footerLink: { color: "#6366f1", textDecoration: "none", fontWeight: "600" },
// };

// export default ProfilePage;
